import { ReactNode, useState } from 'react';
import {
  Stack,
  Modal,
  Card,
  Typography,
  Divider,
  MenuItem,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import { mixpanelTrack } from 'src/utils/analytics';
import { toast } from 'sonner';
import { Tag } from 'src/@types/tag';
import { ActivityFeedExportParams } from 'src/@types/reports';
import { useCreateActivityFeedExport } from 'src/hooks/query/reportsHooks';
import { useTags } from 'src/hooks/query/tagHooks';

type RenderProps = {
  openExportModal: () => void;
};

type Props = {
  exportParams: ActivityFeedExportParams;
  children: (props: RenderProps) => ReactNode;
};

export default function ActivityFeedExportModal({ exportParams, children }: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const createExportJob = useCreateActivityFeedExport();
  const { data: tags, isPending: tagsPending } = useTags();

  const isExporting = createExportJob.isPending;

  const handleClose = () => {
    if (isExporting) return;

    setModalOpen(false);
    setSelectedTags([]);
  };

  const handleExport = async () => {
    if (isExporting) return;

    mixpanelTrack('Exported Activity Feed', {
      selected_tags: selectedTags,
    });

    await createExportJob.mutateAsync(
      {
        ...exportParams,
        voter_tag_ids: selectedTags.length ? selectedTags.map((tag) => tag.id) : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Activity feed export started. You will be notified when it is ready.');
          setModalOpen(false);
          setSelectedTags([]);
        },
        onError: () => {
          toast.error('Failed to start export. Please try again.');
        },
      }
    );
  };

  return (
    <>
      {children({
        openExportModal: () => setModalOpen(true),
      })}
      <Modal
        open={modalOpen}
        onClose={() => {
          if (!isExporting) handleClose();
        }}
      >
        <Card
          sx={{
            width: 700,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'absolute',
            left: '50%',
            top: '10%',
            transform: 'translate(-50%, 0)',
            borderRadius: 2,
            padding: 3,
          }}
        >
          <Stack spacing={3}>
            <Typography variant="h6">Activity Feed Export</Typography>
            <Divider />
            <Stack spacing={2}>
              <Stack direction="column" spacing={1}>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1">Universe Tags (Optional)</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select tags you want included in the export.
                  </Typography>
                </Stack>
                <Autocomplete
                  loading={tagsPending}
                  disabled={isExporting}
                  multiple
                  disableCloseOnSelect
                  options={tags || []}
                  getOptionLabel={(tag) => tag.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options: Tag[], params) => {
                    return options.filter((option) =>
                      option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                    );
                  }}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <MenuItem key={option.id || key} {...rest}>
                        {option.name}
                      </MenuItem>
                    );
                  }}
                  value={selectedTags}
                  onChange={(_event, newValue) => {
                    setSelectedTags(newValue);
                  }}
                  renderInput={(params) => <TextField {...params} placeholder="Add a tag" />}
                />
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Button color="inherit" variant="soft" disabled={isExporting} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                loading={isExporting}
                onClick={handleExport}
              >
                Export
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Modal>
    </>
  );
}
